import React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { arrayRemoveAll, FieldArray, formValueSelector } from 'redux-form'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// components
import MappedProperty from 'components/Modals/SystemProcessModal/Form/components/Transactions/components/Transaction/components/MappedProperty'
import RelatedFormsSelect from 'components/Modals/SystemProcessModal/Form/components/Transactions/components/Transaction/components/RelatedFormsSelect'
// graphql
import { RISK_TEMPLATE_QUERY } from 'graphql/risk/queries'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// constants
import { Forms, FROM_PARENT_FIELD, SELECT_GROUP_LABELS } from 'constants/index'
// types
import { FieldType } from 'types/graphql'
// views
import { Title, Wrapper } from './views'
// utils
import get from 'lodash/get'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

interface Props {
  field: string
}

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

const CreateRelatedForm: React.FC<Props> = ({ field }) => {
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const { basicFields } = React.useContext(RiskTemplateAPIContext)

  const riskTemplateId = useSelector((state) => selector(state, `${field}.riskTemplateId`))?.value

  const [fields, setFields] = React.useState([])

  const getRiskTemplateFields = async (id: string) => {
    try {
      const riskTemplateFields = get(
        await client.query({
          fetchPolicy: 'network-only',
          query: RISK_TEMPLATE_QUERY,
          variables: { filter: { ids: [id] }, pagination: { pageTake: 1, pageSkip: 0 } }
        }),
        'data.riskTemplates.data[0].fields'
      )

      setFields(riskTemplateFields)
    } catch (e) {
      console.log('[Schedule.getRiskTemplateFields]: ', parseGraphQLErrorMessages(e))
    }
  }

  const onFormChange = () => {
    dispatch(arrayRemoveAll(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.fieldValues`))
  }

  const mainFormFields = basicFields
    .filter((field) => field.fieldType === FieldType.Lookup)
    .map((field) => ({
      label: field.label,
      value: FROM_PARENT_FIELD,
      sourceFieldId: field.id
    }))

  const prepareLookupOptions = (options) =>
    options.map((item) => {
      if (item.label === SELECT_GROUP_LABELS.FIELDS) {
        return { ...item, options: [...mainFormFields, ...item.options] }
      }
      return item
    })

  React.useEffect(() => {
    if (riskTemplateId) {
      getRiskTemplateFields(riskTemplateId)
    }
  }, [riskTemplateId])

  return (
    <Wrapper>
      <RelatedFormsSelect field={field} onChange={onFormChange} />
      <Title>Mapped properties:</Title>
      <FieldArray<any>
        templateFields={fields}
        dueDateNowOnly
        component={MappedProperty as any}
        name={`${field}.fieldValues`}
        lookupProps={{ prepareOptions: prepareLookupOptions }}
      />
    </Wrapper>
  )
}

export default CreateRelatedForm
